import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { combineLatest, filter, lastValueFrom } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { environment } from "../../environments/environment";
import { Quiz } from "../models/quiz.model";
import { isCourseNotSelected, getCourseSelected } from "src/app/components/members/courses/courses.selector";
import { select, Store } from "@ngrx/store";
import { CourseState } from "src/app/components/members/courses/courses.reducer";

import { QuizTestsState } from "src/app/components/members/quiz-tests/quiz-tests.model";
import { getSignedInUser } from "src/app/components/allusers/signin/signin.selector";
import { SignInState } from "src/app/components/allusers/signin/signin.model";
import { Course } from "../models/course.model";
import { ChaptersService } from "./chapters.service";
import { NGXLogger } from "ngx-logger";
import { QuizTestsActionTypes, InitializationAction } from "src/app/components/members/quiz-tests/quiz-tests.actions";

@Injectable({
  providedIn: "root",
})
export class QuizesService {
  baseURL: string = environment.apiUrl;
  currentCourse: Course;
  courseQuizzes: { [key: number]: Quiz[] } = {};

  constructor(private http: HttpClient,
    private courseStore: Store<CourseState>,
    private quizStore: Store<QuizTestsState>,
    private loginStore: Store<SignInState>,
    private chapterService: ChaptersService,
    private logger: NGXLogger
  ) {
    // Keep only the currently selected course's quizzes cached: whenever the
    // selected course changes, drop everything so subsequent lookups fetch fresh
    // quizzes for the new course (registered first so it clears before the
    // combineLatest below repopulates the new course's final quiz).
    this.courseStore
      .pipe(
        select(getCourseSelected),
        map(course => (course ? course.id : null)),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.courseQuizzes = {};
      });

    this.courseStore.pipe(select(isCourseNotSelected)).subscribe(() => {
      this.currentCourse = null;
      this.quizStore.dispatch( new InitializationAction({
        currentState: QuizTestsActionTypes.Initialization        
      } as QuizTestsState));
    });

    combineLatest([
      this.loginStore.pipe(select(getSignedInUser)),
      this.courseStore.pipe(select(getCourseSelected), filter(course => course !== null))]).subscribe(async ([user, course]) => { 
          this.logger.info(`Getting final quiz, course: ${course.id}`);
          this.currentCourse = course;          
          const finalChapter = await this.chapterService.getFinalChapterBy(course.id);
          const quizzes = await this.findByCourseIdAndChapterId(course.id, finalChapter.ID);
          if (quizzes && quizzes.length < 1) {
            this.logger.error(`Final quiz not found`, quizzes);
          }
        
          this.quizStore.dispatch(new InitializationAction({
            currentState: QuizTestsActionTypes.Initialization,
            finalQuiz: quizzes[0],
          } as QuizTestsState));
    });
  }

  /** Lazy/paginated quiz list scoped server-side to a single course (admin quiz manager). */
  async findAllPagedByCourse(courseId: number, params: { first?: number; rows: number; sortField?: string; sortOrder?: number; filters?: any }):
    Promise<{ data: Quiz[]; totalRecords: number }> {
    let httpParams: any = { rows: `${params.rows}` };
    if (params.first != null) httpParams.first = `${params.first}`;
    if (params.sortField) httpParams.sortField = params.sortField;
    if (params.sortOrder != null) httpParams.sortOrder = `${params.sortOrder}`;
    if (params.filters) httpParams.filters = encodeURIComponent(JSON.stringify(params.filters));

    return await lastValueFrom(
      this.http.get<{ data: Quiz[]; totalRecords: number }>(`${environment.apiUrl}/courses/${courseId}/quizzes`, { params: httpParams })
    );
  }

  /** Same as findAllPaged, but scoped server-side to a single course + chapter (admin quiz manager). */
  async findAllPagedByCourseAndChapter(courseId: number, chapterId: number, params: { first?: number; rows: number; sortField?: string; sortOrder?: number; filters?: any }):
    Promise<{ data: Quiz[]; totalRecords: number }> {
    let httpParams: any = { rows: `${params.rows}` };
    if (params.first != null) httpParams.first = `${params.first}`;
    if (params.sortField) httpParams.sortField = params.sortField;
    if (params.sortOrder != null) httpParams.sortOrder = `${params.sortOrder}`;
    if (params.filters) httpParams.filters = encodeURIComponent(JSON.stringify(params.filters));

    return await lastValueFrom(
      this.http.get<{ data: Quiz[]; totalRecords: number }>(`${environment.apiUrl}/courses/${courseId}/chapters/${chapterId}/quizzes`, { params: httpParams })
    );
  }

  async findAllPerCourse(courseId: number) {
    if (!this.courseQuizzes[courseId] || this.courseQuizzes[courseId].length === 0) {
      this.courseQuizzes[courseId] = await lastValueFrom(
        this.http
        .get<Array<Quiz>>(
          `${environment.apiUrl}/courses/${courseId}/quizzes`
        )
      );
    }
    return this.courseQuizzes[courseId];
  }

  async findByCourseIdAndChapterId(courseId: number, chapterId: number) {
    if (!this.courseQuizzes[courseId] || this.courseQuizzes[courseId].length === 0) {
      this.courseQuizzes[courseId] = await this.findAllPerCourse(courseId);
    }

    return this.courseQuizzes[courseId].filter(q => q.QuizLocation2 === chapterId.toString());      
  }

  async findByCourseIdAndQuizId(courseId: number, quizId: number) {
    const quizzes = await this.findAllPerCourse(courseId);

    return quizzes.find(q => q.QuizID == quizId);
  }

  async delete(courseId: number, quizId: number) {
    this.courseQuizzes[courseId] = [];
    return await lastValueFrom(
      this.http
        .delete(
          `${environment.apiUrl}/courses/${courseId}/quizzes/${quizId}`
        ))
      ;
  }

  async update(courseId: number, quiz) {
    this.courseQuizzes[courseId] = [];
    return await lastValueFrom(
      this.http
        .put<Quiz>(`${environment.apiUrl}/courses/${courseId}/quizzes/${quiz.QuizID}`, quiz)
    );
  }

  async create(courseId: number, quiz: Quiz): Promise<Quiz> {
    this.courseQuizzes[courseId] = [];
    return await lastValueFrom(
      this.http
        .post<Quiz>(`${environment.apiUrl}/courses/${courseId}/quizzes`, quiz)
    );
  }
}
